<template>
  <b-form @submit="submit">
    <page-header
      :title="`${oferta.codigo || 'Nueva oferta'} ${oferta.descripcion}`"
      :subtitle="cliente.nombre"
      @back="volverAtras"
    >
      <div class="d-flex justify-content-between" style="gap: 2em">
        <div class="nice-box">
          <table>
            <tr>
              <td>Oferta</td>
              <th class="text-right">
                {{ oferta.codigo }}
                v{{ oferta.numeroVersion }}
              </th>
            </tr>
            <tr>
              <td>Fecha Oferta</td>
              <th class="text-right">{{ fechaOferta }}</th>
            </tr>
            <tr>
              <td>Primera versión</td>
              <th class="text-right">{{ fechaPrimeraVersion }}</th>
            </tr>
            <tr>
              <td>Fecha Firma</td>
              <th class="text-right">{{ fechaFirma }}</th>
            </tr>
            <tr>
              <td>Cuota Anual</td>
              <th class="text-right">{{ totalCuotaAnualEnEuros }} €</th>
            </tr>
            <tr>
              <td>Servicios</td>
              <th class="text-right">{{ totalServiciosEnEuros }} €</th>
            </tr>
          </table>
        </div>
        <badges-estado />
      </div>
    </page-header>

    <!-- Cuerpo de la oferta -->
    <b-card no-body>
      <b-tabs card content-class="mt-4">
        <tab-datos-oferta />
        <tab-modulos />
        <tab-servicios />
        <tab-info-oferta />
      </b-tabs>
    </b-card>

    <!-- La botonera de guardado -->
    <b-row>
      <b-col md="8" class="pt-2">
        <b-button-group>
          <b-button
            :disabled="oferta.borrador == 1"
            size="sm"
            variant="danger"
            @click="enviarVersionPapelera(oferta.id, oferta.descripcion)"
          >
            <v-icon name="trash-2" width="16" />

            Enviar a Papelera
          </b-button>
        </b-button-group>

        <b-button-group class="ml-2">
          <b-button variant="primary" size="sm" :to="`/ofertas/editarContenido/${oferta.id}`">
            <v-icon name="file-text" width="16" /> Editar Contenido
          </b-button>

          <b-button variant="primary" size="sm" :to="`/ofertas/editarContenidoModulos/${oferta.id}`">
            <v-icon name="message-square" width="16" /> Editar Módulos
          </b-button>

          <b-button variant="primary" v-show="puedeVerContrato" size="sm" :to="`/ofertas/editarContrato/${oferta.id}`">
            <v-icon name="file-text" width="16" /> Editar Contrato
          </b-button>

          <b-button
            v-show="puedeVerContrato"
            :disabled="false"
            size="sm"
            target="_blank"
            :href="`${backendUrl}/ofertaInforme/informeContrato&ofertaId=${oferta.id}&borrador=true`"
          >
            <v-icon name="printer" width="16" /> Imprimir Borrador Contrato
          </b-button>

          <b-button
            v-show="puedeVerContratoAdmin"
            :disabled="false"
            size="sm"
            target="_blank"
            :href="`${backendUrl}/ofertaInforme/informeContrato&ofertaId=${oferta.id}`"
          >
            <v-icon variant="primary" name="printer" width="16" /> Imprimir Contrato
          </b-button>
        </b-button-group>
        <b-button-group class="ml-2">
          <b-button
            variant="warning"
            size="sm"
            target="_blank"
            :href="`${url}/ofertaInforme/informeComercial&ofertaId=${oferta.id}`"
          >
            <v-icon name="printer" width="16" /> Informe comercial
          </b-button>
        </b-button-group>

        <b-button variant="secondary" class="ml-2" size="sm" :to="`/ofertas/versiones/${oferta.ofertaBaseId}`">
          <v-icon name="clock" width="16" /> Otras versiones
        </b-button>
      </b-col>
      <b-col md="4">
        <botonera-edicion url-cancelar="/ofertas" v-show="!ofertaBloqueada">
          <b-button type="submit" name="aplicar" variant="success"> Aplicar </b-button>
        </botonera-edicion>
      </b-col>
    </b-row>

    <div v-if="oferta.ganada" class="mt-4">
      <b-alert variant="success" show> Esta oferta está GANADA. No se pueden hacer más modificaciones. </b-alert>
    </div>
    <div v-else-if="ofertaBloqueada" class="mt-4">
      <b-alert variant="warning" show> Esta oferta está bloqueada porque {{ causaBloqueo }} </b-alert>
    </div>
  </b-form>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useSessionStore } from "@/stores/SessionStore";
import { useOfertaFormStore } from "@/stores/OfertaFormStore";
import TabDatosOferta from "./TabDatosOferta";
import TabModulos from "./TabModulos";
import TabServicios from "./TabServicios";
import TabInfoOferta from "./TabInfoOferta";
import BadgesEstado from "./DetalleSticky/BadgesEstado";

export default {
  components: {
    TabDatosOferta,
    TabModulos,
    TabServicios,
    TabInfoOferta,
    BadgesEstado,
  },
  props: {
    id: { type: Number, default: null },
  },
  computed: {
    ...mapState(useSessionStore, ["esAdmin"]),
    ...mapState(useOfertaFormStore, [
      "fechaOferta",
      "fechaPrimeraVersion",
      "fechaFirma",
      "borrador",
      "oferta",
      "ofertaValoradaEuro",
      "cliente",
      "soloModulos",
      "soloServicios",
      "tiposDescuento",
      "numModulos",
      "numServicios",
    ]),
    url: () => process.env.VUE_APP_ENDPOINT,
    comboDescuento() {
      return this.tiposDescuento.map((x) => {
        return {
          value: x.descuento,
          text: x.descripcion,
        };
      });
    },
    ofertaBloqueada() {
      return this.oferta.caducada == 1 || this.oferta.borrador == 0;
    },
    causaBloqueo() {
      return this.oferta.caducada == 1 ? "está CADUCADA" : "no es BORRADOR";
    },
    puedeVerContrato() {
      return Boolean(this.oferta.tieneContrato && this.oferta.ganada);
    },
    puedeVerContratoAdmin() {
      return Boolean(this.esAdmin && this.oferta.tieneContrato && this.oferta.ganada);
    },
    backendUrl() {
      return process.env.VUE_APP_ENDPOINT;
    },
    totalCuotaAnualEnEuros() {
      return this.prepararImporte(this.ofertaValoradaEuro.totalCuotaAnualEnEuros);
    },
    totalServiciosEnEuros() {
      return this.prepararImporte(this.ofertaValoradaEuro.totalServiciosEnEuros);
    },
  },
  methods: {
    ...mapActions(useSessionStore, ["setUltimaOfertaModificada"]),
    ...mapActions(useOfertaFormStore, [
      "cargaInicialDeOferta",
      "guardarOfertaCompleta",
      "cargarOfertaCompleta",
      "checkForm",
    ]),
    async submit(event) {
      event.preventDefault();

      const errors = this.checkForm();

      if (errors.length > 0) {
        const errores = errors.join(", ");
        this.$bvToast.toast("Hay errores en el formulario: " + errores, {
          title: "Atención",
          variant: "danger",
        });
        event.preventDefault();
        return;
      }

      const oferta = await this.guardarOfertaCompleta(this.id);
      this.setUltimaOfertaModificada(oferta.id);

      // Si no se pulsa en aplicar, redirijo a la lista de ofertas
      if (event.submitter.name != "aplicar") {
        this.$router.push("/ofertas");
        return;
      }

      // Si estoy creando una oferta, redirijo a la edición
      if (!this.id) {
        this.$router.push("/ofertas/editar/" + oferta.id);
        return;
      }

      // Si estoy editando la oferta, recargo la oferta completa
      this.cargarOfertaCompleta(this.id);
      this.mostrarTostadaOk();
    },
    mostrarTostadaOk() {
      this.$bvToast.toast("Se ha actualizado la oferta", {
        title: "Atención",
        variant: "success",
      });
    },
    volverAtras() {
      this.$router.push("/ofertas");
    },
    prepararImporte(value) {
      const options = { maximumFractionDigits: 0, useGrouping: true };
      return Intl.NumberFormat("es-ES", options).format(Math.round(value));
    },
    async confirmarSalida() {
      const t = "¿Está seguro?";
      const ok = await this.$bvModal.msgBoxConfirm(t, { okTitle: "Aceptar", cancelTitle: "Cancelar" });
      if (ok) {
        this.$router.push("/ofertas");
      }
    },
  },
  data() {
    return {};
  },
  beforeRouteLeave(to, from) {
    // TODO: confirmar al salir sin guardar
  },
};
</script>
